<template>
    <BootstrapModal
        :id="modalId"
        hide-footer
        @close="resetModal()">
        <template #modal-title>
            {{ $t('File') }}: <span v-if="modalTitleFileName">{{file.name}}</span>
        </template>
        <div class="file-edit-modal-container">
            <template v-if="form && !loading">
                <EInput id="name-input" v-model="form.name" class="mb-3" :validation="$v.form.name">
                    <template #label>
                        {{ $t('Name') }}
                    </template>
                </EInput>
                <label for="view-select" class="d-flex align-items-center">
                    <span>
                        {{ $t('Add target groups who can view the file') }}
                        - <small>{{ $t('Optional') }}</small>
                    </span>
                    <ETooltip id="targetViewTip" class="ml-2" :inline="false">
                        <div class="text-left">{{ $t('All target groups added here can view and download the file.') }}</div>
                    </ETooltip>
                </label>
                <ESelect
                    id="view-select"
                    v-model="form.view"
                    :options="userTargetGroups"
                    :placeholder="$t('Choose')"
                    searchable
                    multiple
                    track-by="unique_id"
                    :close-on-select="false"
                    :custom-label="target => target.name + ' (' + ($t(typeTranslations(target.type))) + ')'"
                    class="mb-3"/>

                <label for="edit-select" class="d-flex align-items-center">
                    <span>
                        {{ $t('Add target groups who can edit the file') }}
                        - <small>{{ $t('Optional') }}</small>
                    </span>
                    <ETooltip id="targetEditTip" class="ml-2" :inline="false">
                        <div class="text-left">{{ $t('All target groups added here can view and download the file.') }}</div>
                        <div class="text-left mt-2">{{ $t('They can also edit and delete the file.') }}</div>
                    </ETooltip>
                </label>
                <ESelect
                    id="edit-select"
                    v-model="form.edit"
                    :options="userTargetGroups"
                    :placeholder="$t('Choose')"
                    searchable
                    multiple
                    track-by="unique_id"
                    :close-on-select="false"
                    :custom-label="target => target.name + ' (' + ($t(typeTranslations(target.type))) + ')'"
                    class="mb-3"/>

                <div class="d-flex justify-content-end mt-3">
                    <button class="btn btn-secondary mr-2" @click="closeModal()">
                        {{ $t('Cancel') }}
                    </button>
                    <EBtnLoader :is-submit-loading="isSubmitLoading" @clicked="save">
                        <span>{{ $t('Save') }}</span>
                    </EBtnLoader>
                </div>
            </template>
            <ELoader v-else/>
        </div>
    </BootstrapModal>
</template>

<script>
import {alphaNumFileCustom} from '@/validators';
import {capitalize} from '@/filters';
import {mapGetters} from 'vuex';
import {minLength, required} from 'vuelidate/lib/validators';
import BootstrapModal from '@/common/components/modals/BootstrapModal.vue';
import DocumentService from '@services/document.service';
import EventBus from '@/utils/eventBus';

export default {
    name: 'FileModal',
    data() {
        return {
            modalId: 'folder-file-modal',
            file: null,
            form: {
                name: '',
                edit: [],
                view: []
            },
            modalTitleFileName: '',
            availableTargetGroups: [],
            loading: true,
            files: [],
            isEdit: false,
            isSubmitLoading: false,
            index: false,
            mainFolder: ''
        };
    },
    computed: {
        ...mapGetters([
            'currentEmployee',
            'currentGuardian',
            'userTargetGroups',
            'activeKindergartenId',
            'userTargetIdentities',
            'folder',
            'folderFiles',
            'subFolders'
        ])
    },
    methods: {
        openModal() {
            EventBus.emit('show-modal', this.modalId);
            this.$store.dispatch('GET_USER_TARGETGROUPS').then(() => {
                this.form.name = this.file.name;
                this.removeExtension();
                this.form.filename = this.file.filename;
                this.modalTitleFileName = this.file.filename;
                this.loading = false;
            });
            DocumentService.getDocumentAcl(this.activeKindergartenId, this.file.id).then(res => {
                this.form.edit = res.edit;
                this.form.view = res.view;
            });
        },
        typeTranslations(type) {
            if (type === 'group') {
                return 'Target group';
            } else {
                return capitalize(type);
            }
        },
        save() {
            this.$v.$touch();
            if (!this.$v.$invalid) {
                this.isSubmitLoading = true;
                this.removeExtension();
                this.$store.dispatch('UPDATE_DOCUMENT_FILE', this.form).then(() => {
                    this.$toastr.s(this.$t('File updated'));
                    this.closeModal();
                }).finally(() => {
                    this.isSubmitLoading = false;
                });
            }
        },
        closeModal() {
            EventBus.emit('hide-modal', this.modalId);
        },
        resetModal() {
            this.form = {
                name: '',
                edit: [],
                view: []
            };
            this.loading = true;
            this.modalTitleFileName = '';
        },
        removeExtension() {
            this.form.name = this.form.name.replace(/\.(jpg|JPG|png|PNG|txt|TXT|xlsx|XLSX|docx|DOCX|doc|DOC|pdf|PDF)$/g, '');
        }
    },
    components: {
        BootstrapModal
    },
    created() {
        EventBus.on('show-edit-file-modal', file => {
            this.file = file;
            this.openModal();
        });
    },
    destroyed() {
        EventBus.off('show-edit-file-modal');
    },
    validations: {
        form: {
            name: {
                required,
                minLength: minLength(1),
                alphaNumFileCustom
            }
        }
    }
};
</script>

<style scoped>
    .file-edit-modal-container {
        min-height: 197px;
    }
</style>
